/**
 * Who owns the audio bytes cached on this device (SEC0901-D-02, amplification).
 *
 * `AudioCache` (`ssi-audio-cache-v2`) is keyed by audio id alone, with no
 * learner in the key — so a departing learner's downloaded audio is readable by
 * whoever uses the device next. The obvious fix, "wipe the audio cache on
 * sign-out", is WORSE than the bug it closes: Offline Mode is an explicit,
 * deliberate, potentially multi-gigabyte download, and a learner signing out on
 * their own phone would lose all of it every time.
 *
 * So the rule here is narrower and matches the actual threat: purge only when
 * the device's signed-in identity ACTUALLY CHANGES — a different learner signs
 * in than the one whose audio is cached. A same-account sign-out/sign-in cycle
 * keeps every byte. A guest-cached set (no recorded owner) is adopted rather
 * than purged: those bytes are free/preview content by definition, since the
 * uuids that address anything else come from the entitlement-gated bundle and
 * cycles endpoints.
 *
 * The owner marker lives in localStorage rather than IndexedDB so that reading
 * it costs nothing on the boot path and it cannot itself be the thing that
 * fails; a missing marker degrades to "adopt", never to "purge".
 */

import { getAudioCache } from './createAudioCache'
import { OWNER_KEY } from './AudioCache'

// The purge never holds sign-in hostage to a wedged IndexedDB.
const CLEAR_TIMEOUT_MS = 10_000

export type AudioCacheOwnerOutcome = 'kept' | 'adopted' | 'purged' | 'unavailable'

function readOwner(): string | null {
  try {
    return localStorage.getItem(OWNER_KEY)
  } catch {
    return null
  }
}

function writeOwner(id: string): void {
  try {
    localStorage.setItem(OWNER_KEY, id)
  } catch {
    // A device that cannot persist the marker re-adopts next boot: at worst
    // one extra no-op reconcile, never an unwanted purge.
  }
}

/**
 * Reconcile the cached audio against the identity now signed in on this device.
 *
 * Call it whenever an auth session resolves to a user. Idempotent and cheap:
 * the common case is one localStorage read and nothing else.
 */
export async function reconcileAudioCacheOwner(identityId: string | null): Promise<AudioCacheOwnerOutcome> {
  if (!identityId) return 'unavailable' // signed out — never a reason to purge
  const previous = readOwner()
  if (previous === identityId) return 'kept'
  if (previous === null) {
    writeOwner(identityId)
    return 'adopted'
  }
  console.warn('[AudioCacheOwner] signed-in identity changed on this device — clearing cached audio')
  // EMPTY THE STORE, NEVER DELETE THE DATABASE (job #168). deleteDatabase is
  // blocked by any other tab holding a connection, and a blocked delete stays
  // queued: every later open on the origin, this tab's included, waits behind
  // it. The new learner's player then never stored a clip and streamed every
  // one (Tom's Safari, 2026-10-01, 0 cache hits in 160 plays). A readwrite
  // clear goes through whoever else is connected.
  let cleared = false
  let deadline: ReturnType<typeof setTimeout> | undefined
  try {
    cleared = await Promise.race([
      getAudioCache().clearAll().then(() => true),
      new Promise<boolean>((resolve) => { deadline = setTimeout(() => resolve(false), CLEAR_TIMEOUT_MS) }),
    ])
  } catch {
    cleared = false
  } finally {
    clearTimeout(deadline)
  }
  // The store could not be emptied. NEVER deleteDatabase as a fallback (job
  // #202): that is the blocked delete again. Hide the old learner's rows and
  // sweep them record by record instead; neither can block another open.
  if (!cleared) getAudioCache().markStale()
  writeOwner(identityId)
  return 'purged'
}

/** Test seam / recovery path: forget who owns the cache without touching it. */
export function forgetAudioCacheOwner(): void {
  try {
    localStorage.removeItem(OWNER_KEY)
  } catch {
    // nothing to do
  }
}
